export interface ZipEntryInfo {
  readonly name: string;
  readonly method: number;
  readonly crc32: string;
  readonly compressedSize: number;
  readonly uncompressedSize: number;
  readonly localOffset: number;
  readonly isDirectory: boolean;
}

const ZIP_LOCAL_FILE_HEADER = 0x04034b50;
const ZIP_CENTRAL_FILE_HEADER = 0x02014b50;
const ZIP_END_OF_CENTRAL_DIRECTORY = 0x06054b50;
const MAX_EOCD_SEARCH_BYTES = 65_557;

export function isZipArchive(bytes: Uint8Array): boolean {
  return bytes.byteLength >= 22 &&
    bytes[0] === 0x50 &&
    bytes[1] === 0x4b &&
    bytes[2] === 0x03 &&
    bytes[3] === 0x04 &&
    findEndOfCentralDirectory(bytes) >= 0;
}

export function listZipEntries(bytes: Uint8Array): ZipEntryInfo[] {
  const eocdOffset = findEndOfCentralDirectory(bytes);
  if (eocdOffset < 0) throw new Error('ZIP end-of-central-directory record not found');

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const entryCount = view.getUint16(eocdOffset + 10, true);
  const centralOffset = view.getUint32(eocdOffset + 16, true);
  const entries: ZipEntryInfo[] = [];
  let cursor = centralOffset;

  for (let index = 0; index < entryCount; index += 1) {
    if (view.getUint32(cursor, true) !== ZIP_CENTRAL_FILE_HEADER) {
      throw new Error(`Invalid ZIP central directory header at offset ${cursor}`);
    }
    const flags = view.getUint16(cursor + 8, true);
    const method = view.getUint16(cursor + 10, true);
    const crc = view.getUint32(cursor + 16, true);
    const compressedSize = view.getUint32(cursor + 20, true);
    const uncompressedSize = view.getUint32(cursor + 24, true);
    const nameLength = view.getUint16(cursor + 28, true);
    const extraLength = view.getUint16(cursor + 30, true);
    const commentLength = view.getUint16(cursor + 32, true);
    const localOffset = view.getUint32(cursor + 42, true);
    const nameBytes = bytes.subarray(cursor + 46, cursor + 46 + nameLength);
    const name = decodeZipName(nameBytes, flags);
    entries.push({
      name,
      method,
      crc32: hex32(crc),
      compressedSize,
      uncompressedSize,
      localOffset,
      isDirectory: name.endsWith('/'),
    });
    cursor += 46 + nameLength + extraLength + commentLength;
  }

  return entries;
}

export async function extractZipEntry(
  archive: Uint8Array,
  entry: ZipEntryInfo,
): Promise<Uint8Array> {
  const view = new DataView(archive.buffer, archive.byteOffset, archive.byteLength);
  if (view.getUint32(entry.localOffset, true) !== ZIP_LOCAL_FILE_HEADER) {
    throw new Error(`Invalid ZIP local header for ${entry.name}`);
  }
  const nameLength = view.getUint16(entry.localOffset + 26, true);
  const extraLength = view.getUint16(entry.localOffset + 28, true);
  const dataStart = entry.localOffset + 30 + nameLength + extraLength;
  const dataEnd = dataStart + entry.compressedSize;
  const compressed = archive.subarray(dataStart, dataEnd);

  if (entry.method === 0) {
    if (compressed.byteLength !== entry.uncompressedSize) {
      throw new Error(`${entry.name} stored size mismatch`);
    }
    return new Uint8Array(compressed);
  }
  if (entry.method !== 8) {
    throw new Error(`${entry.name} uses unsupported ZIP compression method ${entry.method}`);
  }
  if (typeof DecompressionStream !== 'function') {
    throw new Error('This browser cannot inflate ZIP entries locally');
  }

  const stream = new Blob([copyToArrayBuffer(compressed)])
    .stream()
    .pipeThrough(new DecompressionStream('deflate-raw'));
  const inflated = new Uint8Array(await new Response(stream).arrayBuffer());
  if (inflated.byteLength !== entry.uncompressedSize) {
    throw new Error(`${entry.name} inflated size mismatch`);
  }
  return inflated;
}

function findEndOfCentralDirectory(bytes: Uint8Array): number {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const minOffset = Math.max(0, bytes.byteLength - MAX_EOCD_SEARCH_BYTES);
  for (let offset = bytes.byteLength - 22; offset >= minOffset; offset -= 1) {
    if (view.getUint32(offset, true) === ZIP_END_OF_CENTRAL_DIRECTORY) return offset;
  }
  return -1;
}

function decodeZipName(bytes: Uint8Array, flags: number): string {
  const utf8 = (flags & 0x0800) !== 0;
  if (utf8) return new TextDecoder('utf-8', { fatal: false }).decode(bytes);
  return String.fromCharCode(...bytes);
}

function hex32(value: number): string {
  return (value >>> 0).toString(16).padStart(8, '0');
}

function copyToArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}
